import { dashboardViewDefinitionSchema, } from "./channels.js";
import { DASHBOARD_DEFAULT_LAYOUT } from "./dashboard-layout.js";
const METRIC_HINTS = ["amount", "value", "total", "price", "revenue", "cost"];
const DATE_HINTS = ["date", "created", "issued", "start"];
const GROUP_HINTS = ["status", "stage", "category", "type"];
// How tall each part is, in rows of the default grid: a figure 108px, a chart 308px, the list 348px.
const FIGURE_ROWS = 3;
const CHART_ROWS = 8;
const LIST_ROWS = 9;
/*
 * The one generated starter dashboard, shared by the control plane (default
 * dashboard data) and the app ("New view" template) so both surfaces begin
 * from the same layout. Rows always fill the full 12 columns symmetrically:
 * a KPI band, then paired charts, then the record list.
 */
export function defaultDashboardDefinition(objectTypeId, fields) {
    const metric = preferredField(fields, ["currency", "number", "percent"], METRIC_HINTS);
    const date = preferredField(fields, ["date", "datetime"], DATE_HINTS);
    const group = preferredField(fields, ["select", "multiselect"], GROUP_HINTS);
    const widgets = [];
    let chartRow = 0;
    if (metric) {
        // Three KPI tiles share the row evenly.
        widgets.push({
            id: "records",
            type: "metric",
            title: "Records",
            position: { x: 0, y: 0, w: 4, h: FIGURE_ROWS },
            metric: { aggregate: "count" },
        }, {
            id: `total-${metric.key}`,
            type: "metric",
            title: `Total ${metric.label}`,
            position: { x: 4, y: 0, w: 4, h: FIGURE_ROWS },
            metric: { aggregate: "sum", fieldKey: metric.key },
        }, {
            id: `average-${metric.key}`,
            type: "metric",
            title: `Average ${metric.label}`,
            position: { x: 8, y: 0, w: 4, h: FIGURE_ROWS },
            metric: { aggregate: "average", fieldKey: metric.key },
        });
        chartRow = FIGURE_ROWS;
    }
    else if (group) {
        // Without a measure the count tile pairs with the mix chart on one row.
        widgets.push({
            id: "records",
            type: "metric",
            title: "Records",
            position: { x: 0, y: 0, w: 4, h: CHART_ROWS },
            metric: { aggregate: "count" },
        });
    }
    else {
        widgets.push({
            id: "records",
            type: "metric",
            title: "Records",
            position: { x: 0, y: 0, w: 4, h: FIGURE_ROWS },
            metric: { aggregate: "count" },
        });
        chartRow = FIGURE_ROWS;
    }
    if (group) {
        widgets.push({
            id: `${group.key}-mix`,
            type: metric ? "bar" : "breakdown",
            title: group.label,
            position: metric
                ? { x: 0, y: chartRow, w: date ? 6 : 12, h: CHART_ROWS }
                : { x: 4, y: 0, w: 8, h: CHART_ROWS },
            ...(metric
                ? { metric: { aggregate: "sum", fieldKey: metric.key } }
                : { metric: { aggregate: "count" } }),
            dimension: { fieldKey: group.key },
            variant: "bar",
            limit: 12,
            sort: { direction: "desc", mode: "value" },
        });
        if (!metric)
            chartRow = CHART_ROWS;
    }
    if (date) {
        const paired = Boolean(group && metric);
        widgets.push({
            id: `${date.key}-trend`,
            type: "time_series",
            title: `${metric?.label ?? "Records"} over time`,
            position: { x: paired ? 6 : 0, y: chartRow, w: paired ? 6 : 12, h: CHART_ROWS },
            metric: metric
                ? { aggregate: "sum", fieldKey: metric.key }
                : { aggregate: "count" },
            dimension: { fieldKey: date.key, bucket: "month" },
            limit: 12,
        });
    }
    const chartRowUsed = Boolean((group && metric) || date);
    const listY = chartRowUsed ? chartRow + CHART_ROWS : chartRow;
    widgets.push({
        id: "recent-records",
        type: "record_list",
        title: "Recent records",
        position: { x: 0, y: listY, w: 12, h: LIST_ROWS },
        columns: fields.slice(0, 5).map((field) => field.key),
        limit: 10,
        ...(date
            ? { sort: { fieldKey: date.key, direction: "desc", mode: "date" } }
            : {}),
    });
    return dashboardViewDefinitionSchema.parse({
        version: 1,
        objectTypeId,
        layout: { ...DASHBOARD_DEFAULT_LAYOUT },
        filters: [],
        widgets,
    });
}
function preferredField(fields, types, hints) {
    const candidates = fields.filter((field) => types.includes(field.type));
    return candidates.find((field) => {
        const searchable = `${field.key} ${field.label}`.toLowerCase();
        return hints.some((hint) => searchable.includes(hint));
    }) ?? candidates[0];
}
